import { useCallback, useContext, useEffect, useState } from "react";
import { callFunction } from "../api/callFunction";
import { GlobalContext } from "../context/GlobalContext";
import { SecondaryButtonSmall } from "../components/Button";
import { showToast } from "../utils/showToast";

// Super-admin KPI dashboard. One callable (getAdminKpis) returns the whole
// payload, sliced by { range, regionId }. Growth leads (per the product call):
// total members + new-signups trend sit at the top, with revenue, marketplace
// supply, engagement and trust/safety below.

const RANGES = [
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
  { id: "90d", label: "90D" },
  { id: "all", label: "All" },
];

const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const fmtUSD = (n) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtPct = (n) => (n == null ? "—" : `${Math.round(n * 100)}%`);

// --- Inline SVG sparkline (no charting dep) ----------------------------------
const Sparkline = ({ points, colorClass = "text-primary", height = 34 }) => {
  if (!points || points.length === 0) {
    return <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">Trend builds from nightly snapshots</p>;
  }
  const vals = points.map((p) => Number(p.count) || 0);
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const span = max - min || 1;
  const w = 100;
  const stepX = points.length > 1 ? w / (points.length - 1) : 0;
  const y = (v) => height - ((v - min) / span) * (height - 6) - 3;
  const coords = points.map((p, i) => `${(i * stepX).toFixed(2)},${y(Number(p.count) || 0).toFixed(2)}`);
  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      className={`mt-2 w-full ${colorClass}`}
      style={{ height }}
      aria-hidden="true"
    >
      {points.length === 1 ? (
        <circle cx="50" cy={y(vals[0])} r="2" fill="currentColor" />
      ) : (
        <polyline
          points={coords.join(" ")}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
};

const NationalBadge = () => (
  <span
    className="inline-flex rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-300"
    title="Platform-wide — this metric isn't tied to a region"
  >
    platform-wide
  </span>
);

const KpiCard = ({ label, value, sub, series, colorClass, national, regional }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      {national && regional ? <NationalBadge /> : null}
    </div>
    <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    {sub ? <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{sub}</p> : null}
    {series !== undefined ? <Sparkline points={series} colorClass={colorClass} /> : null}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="mb-3 mt-7 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">{children}</h2>
);

// Horizontal proportional bars for the funnel / decision mix.
const BarList = ({ rows, colorClass = "bg-primary" }) => {
  const max = Math.max(1, ...rows.map((r) => Number(r.value) || 0));
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3">
          <span className="w-28 shrink-0 text-xs text-gray-500 dark:text-gray-400">{r.label}</span>
          <div className="h-2.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-700">
            <div
              className={`h-2.5 rounded-full ${r.colorClass || colorClass}`}
              style={{ width: `${Math.max(2, ((Number(r.value) || 0) / max) * 100)}%` }}
            />
          </div>
          <span className="w-10 shrink-0 text-right text-xs font-medium text-gray-700 dark:text-gray-200">
            {fmtInt(r.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const Panel = ({ children, className = "" }) => (
  <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 ${className}`}>
    {children}
  </div>
);

// --- Region portfolio --------------------------------------------------------
// Launch-status badge styling, matching the /regions admin view.
const REGION_STATUS_STYLES = {
  live: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  beta: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  requested: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  paused: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  unassigned: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

const StatusPill = ({ status }) => (
  <span
    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
      REGION_STATUS_STYLES[status] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
    }`}
  >
    {status}
  </span>
);

// Expansion scoreboard: status counts + a per-region table (events/GMV/hosts/
// pending ideas are for the selected range; demand + pending are current).
const RegionPortfolio = ({ portfolio, range, selectedRegionId }) => {
  if (!portfolio) return null;
  const { statusCounts = {}, totalRequestDemand = 0, rows = [], unassigned } = portfolio;
  const tableRows = unassigned ? [...rows, unassigned] : rows;
  const summary = [
    { label: "Live", value: statusCounts.live || 0, colorClass: "text-green-600 dark:text-green-400" },
    { label: "Beta", value: statusCounts.beta || 0, colorClass: "text-blue-600 dark:text-blue-400" },
    { label: "Requested", value: statusCounts.requested || 0, colorClass: "text-amber-600 dark:text-amber-400" },
    { label: "Paused", value: statusCounts.paused || 0, colorClass: "text-gray-500 dark:text-gray-400" },
    { label: "Open demand", value: totalRequestDemand, colorClass: "text-gray-700 dark:text-gray-200" },
  ];
  const th = "py-2 px-3 text-right font-medium";
  return (
    <Panel>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {summary.map((s) => (
            <div key={s.label}>
              <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{s.label}</p>
              <p className={`text-xl font-semibold ${s.colorClass}`}>{fmtInt(s.value)}</p>
            </div>
          ))}
        </div>
        <a href="/regions" className="shrink-0 text-xs font-medium text-primary hover:underline">
          Manage regions →
        </a>
      </div>

      {tableRows.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No regions configured yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                <th className="py-2 pr-3 text-left font-medium">Region</th>
                <th className="py-2 px-3 text-left font-medium">Status</th>
                <th className={th}>Events ran</th>
                <th className={th}>Net GMV</th>
                <th className={th}>Active hosts</th>
                <th className={th}>Pending ideas</th>
                <th className="py-2 pl-3 text-right font-medium">Demand</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {tableRows.map((r) => {
                const selected = selectedRegionId && selectedRegionId !== "all" && r.id === selectedRegionId;
                return (
                  <tr key={r.id} className={selected ? "bg-primary/5" : ""}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-800 dark:text-gray-100">{r.name}</div>
                      {r.status !== "unassigned" && (
                        <div className="text-[11px] text-gray-400 dark:text-gray-500">{r.id}</div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                      {fmtInt(r.eventsRan)}
                      <span className="text-gray-400 dark:text-gray-500">/{fmtInt(r.eventsScheduled)}</span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{fmtUSD(r.gmvUsd)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{fmtInt(r.activeHosts)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{fmtInt(r.pendingSuggestions)}</td>
                    <td className="py-2 pl-3 text-right tabular-nums">
                      {r.requestCount > 0 ? (
                        <span className="font-medium text-amber-600 dark:text-amber-400">{fmtInt(r.requestCount)}</span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
        Events ran / GMV / active hosts are for the selected range ({range}), attributed to a region by each event's
        location; pending ideas and demand (city requests) are current. “Unassigned” = events outside every
        region's catchment radius.
      </p>
    </Panel>
  );
};

const KpiDashboard = () => {
  const { globalState } = useContext(GlobalContext);
  const [range, setRange] = useState("30d");
  const [regionId, setRegionId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await callFunction("getAdminKpis", { range, regionId }, { timeout: 60000 });
      setData(res);
    } catch (err) {
      console.error("getAdminKpis failed", err);
      // The callable gates on the Auth superAdmin custom claim, which is stricter
      // than the panel's vendor-field gate. An operator provisioned only via the
      // vendor field reaches this page but is denied by the callable — give them
      // an actionable message rather than a bare "permission denied".
      const denied =
        err?.code === "functions/permission-denied" ||
        /permission-denied|super admin/i.test(err?.message || "");
      const msg = denied
        ? "Your account isn't provisioned with the superAdmin claim. Run the bootstrap super-admin script (setSuperAdminClaim) for this account, then reload."
        : err.message || "Failed to load KPIs";
      setLoadError(msg);
      showToast(denied ? "Super admin claim required" : err.message || "Failed to load KPIs");
    } finally {
      setLoading(false);
    }
  }, [range, regionId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!globalState.isSuperAdmin) {
    return (
      <div className="mx-auto max-w-6xl p-4 sm:p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">This dashboard is available to super admins only.</p>
      </div>
    );
  }

  const regional = regionId !== "all";
  const regions = data?.regions || [{ id: "all", name: "All regions" }];
  const g = data?.growth;
  const r = data?.revenue;
  const s = data?.supply;
  const e = data?.engagement;
  const sf = data?.safety;
  const funnel = s?.suggestionFunnel;

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Company KPIs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            The health of the marketplace at a glance{loading ? " · updating…" : ""}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Region selector */}
          <select
            value={regionId}
            onChange={(ev) => setRegionId(ev.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {regions.map((rg) => (
              <option key={rg.id} value={rg.id}>
                {rg.name}
                {rg.status && rg.status !== "all" && rg.status !== "live" ? ` (${rg.status})` : ""}
              </option>
            ))}
          </select>

          {/* Range segmented control */}
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
            {RANGES.map((rg) => (
              <button
                key={rg.id}
                onClick={() => setRange(rg.id)}
                className={`px-3 py-1.5 text-sm font-medium ${
                  range === rg.id
                    ? "bg-primary text-white"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {rg.label}
              </button>
            ))}
          </div>

          <SecondaryButtonSmall onClick={load} loading={loading}>
            Refresh
          </SecondaryButtonSmall>
        </div>
      </div>

      {loadError && !data ? (
        <Panel className="border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
        </Panel>
      ) : !data ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading KPIs…</p>
      ) : (
        <>
          {/* HERO — member growth leads */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <KpiCard
              label="Total members"
              value={fmtInt(g?.totalMembers?.value)}
              sub="Everyone who has ever signed up"
              series={g?.membersSeries}
              colorClass="text-primary"
              national
              regional={regional}
            />
            <KpiCard
              label={`New signups · ${range === "all" ? "all time" : range}`}
              value={fmtInt(g?.newSignups?.value)}
              sub="New member accounts in range"
              series={g?.newSignups?.series}
              colorClass="text-emerald-500"
              national
              regional={regional}
            />
          </div>

          {/* GROWTH */}
          <SectionTitle>Growth</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="Total members" value={fmtInt(g?.totalMembers?.value)} national regional={regional} />
            <KpiCard label="New signups" value={fmtInt(g?.newSignups?.value)} sub={range} national regional={regional} />
            <KpiCard label="Total hosts" value={fmtInt(g?.totalHosts)} />
            <KpiCard label="Active hosts" value={fmtInt(s?.activeHosts)} sub="ran/created in range" />
          </div>

          {/* REGIONS — launch portfolio + per-region supply */}
          <SectionTitle>Regions</SectionTitle>
          <RegionPortfolio portfolio={data.regionPortfolio} range={range} selectedRegionId={regionId} />

          {/* REVENUE */}
          <SectionTitle>Revenue</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard
              label="Net event GMV"
              value={fmtUSD(r?.eventGmvUsd)}
              sub="Ticket sales, net of refunds"
              series={r?.gmvSeries}
              colorClass="text-indigo-500"
            />
            <KpiCard
              label="Platform take"
              value={fmtUSD(r?.platformTakeUsd)}
              sub={r?.hostRate != null ? `est. · host keeps ${Math.round(r.hostRate * 100)}%` : "estimate"}
            />
            <KpiCard
              label="Premium subscribers"
              value={fmtInt(r?.activePremiumSubs?.value)}
              sub="Active now"
              national
              regional={regional}
            />
            <KpiCard label="Avg check-ins / ran event" value={s?.eventsRan ? fmtInt(Math.round((e?.totalCheckins || 0) / s.eventsRan)) : "—"} />
          </div>

          {/* MARKETPLACE SUPPLY */}
          <SectionTitle>Marketplace Supply</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard
              label="Events ran"
              value={fmtInt(s?.eventsRan)}
              sub="with real turnout"
              series={s?.eventsRanSeries}
              colorClass="text-rose-500"
            />
            <KpiCard label="Ran-rate" value={fmtPct(s?.ranRate)} sub={`${fmtInt(s?.eventsScheduled)} scheduled`} />
            <KpiCard
              label="Fill rate"
              value={fmtPct(s?.fillRate)}
              sub={s?.fillCoveragePct != null ? `${s.fillCoveragePct}% of ran events set capacity` : null}
            />
            <KpiCard label="Ideas pending" value={fmtInt(funnel?.pendingNow)} sub="awaiting a host" />
          </div>
          <div className="mt-3">
            <Panel>
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Idea funnel · {range}
              </p>
              <BarList
                rows={[
                  { label: "Generated", value: funnel?.generated, colorClass: "bg-sky-400" },
                  { label: "Converted", value: funnel?.converted, colorClass: "bg-emerald-500" },
                  { label: "Pending now", value: funnel?.pendingNow, colorClass: "bg-amber-400" },
                  { label: "Live in catalog", value: funnel?.convertedNow, colorClass: "bg-indigo-500" },
                ]}
              />
              <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
                Generated/Converted are cumulative over the range (from nightly snapshots); Pending/Live reflect the
                current catalog.
              </p>
            </Panel>
          </div>

          {/* ENGAGEMENT */}
          <SectionTitle>Engagement &amp; Retention</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard
              label="Matches made"
              value={fmtInt(e?.matchesMade?.value)}
              sub="Mutual connections in range"
              series={e?.matchesMade?.series}
              colorClass="text-pink-500"
              national
              regional={regional}
            />
            <KpiCard label="RSVPs" value={fmtInt(e?.totalRsvps)} sub="in range (guests)" />
            <KpiCard label="Check-ins" value={fmtInt(e?.totalCheckins)} sub="in range (guests)" />
            <KpiCard
              label="RSVP → attend"
              value={e?.totalRsvps ? fmtPct((e.totalCheckins || 0) / e.totalRsvps) : "—"}
              sub="check-ins / RSVPs"
            />
          </div>

          {/* TRUST & SAFETY */}
          <SectionTitle>Trust &amp; Safety</SectionTitle>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard label="Open arbitration cases" value={fmtInt(sf?.openArbitrations)} sub="not yet resolved" national regional={regional} />
            <Panel className="sm:col-span-2">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Arbitration decisions (all time)
              </p>
              <BarList
                rows={[
                  { label: "Dismissed", value: sf?.decisionMix?.dismissed, colorClass: "bg-gray-400" },
                  { label: "Warning", value: sf?.decisionMix?.warning, colorClass: "bg-amber-400" },
                  { label: "Suspended", value: sf?.decisionMix?.suspended, colorClass: "bg-orange-500" },
                  { label: "Banned", value: sf?.decisionMix?.banned, colorClass: "bg-red-500" },
                ]}
              />
            </Panel>
          </div>

          {/* Footnotes */}
          {Array.isArray(data.notes) && data.notes.length > 0 && (
            <div className="mt-6 space-y-1">
              {data.notes.map((n, i) => (
                <p key={i} className="text-[11px] text-gray-400 dark:text-gray-500">
                  • {n}
                </p>
              ))}
            </div>
          )}
          {data.computedAtMs && (
            <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
              Computed {new Date(data.computedAtMs).toLocaleString()}.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default KpiDashboard;
